export type LegOption = {
  code: string;
  name: string;
};

/** Placeholder character used in the SKU's leg position when no leg is chosen. */
export const NO_LEG_CODE = "X";

export const NO_LEG_OPTION: LegOption = { code: NO_LEG_CODE, name: "No leg" };

/**
 * Leg options mirror the backend leg data (codes A–L). Legs are structural — the code is the
 * final character of a full SKU — so this list is stable and shipped statically rather than
 * fetched. "No leg" is offered for components (e.g. drawer beds) that carry no visible leg.
 */
export const LEG_OPTIONS: LegOption[] = [
  { code: "A", name: "Tapered leg / Walnut" },
  { code: "B", name: "Tapered leg / Natural" },
  { code: "C", name: "Tapered leg / Carbon" },
  { code: "D", name: "Cone leg / Walnut" },
  { code: "E", name: "Cone leg / Natural" },
  { code: "F", name: "Cone leg / Carbon" },
  { code: "G", name: "Metal leg / Nickel" },
  { code: "H", name: "Metal leg / Brass" },
  { code: "I", name: "Metal leg / Carbon" },
  { code: "J", name: "Stiletto leg / Nickel" },
  { code: "K", name: "Stiletto leg / Brass" },
  { code: "L", name: "Stiletto leg / Carbon" },
  NO_LEG_OPTION,
];

// Which leg families each SKU prefix offers (per the client's spec). Codes map to LEG_OPTIONS:
//   Tapered A/B/C · Cone D/E/F · Metal G/H/I · Stiletto J/K/L.
//   - DL*  sofas -> Tapered / Cone / Metal
//   - DB1* beds  -> Tapered / Cone / Stiletto
//   - DB2* beds  -> No leg only
const DL_LEG_CODES = ["A", "B", "C", "D", "E", "F", "G", "H", "I"];
const DB1_LEG_CODES = ["A", "B", "C", "D", "E", "F", "J", "K", "L"];

/**
 * Leg options a given SKU may use, restricted by its prefix. Returns an empty list for SKUs that
 * carry no leg (pillows, chairs, unmatched prefixes). DB2 returns just "No leg" — a single,
 * non-configurable option — so callers can auto-apply it without showing a picker.
 */
export const getLegOptionsForSku = (sku: string): LegOption[] => {
  const s = (sku || "").trim().toUpperCase();

  if (s.startsWith("DL")) return LEG_OPTIONS.filter((option) => DL_LEG_CODES.includes(option.code));
  if (s.startsWith("DB2")) return [NO_LEG_OPTION];
  if (s.startsWith("DB1")) return LEG_OPTIONS.filter((option) => DB1_LEG_CODES.includes(option.code));

  return [];
};

/**
 * Resolve a free-text leg value (from a CSV cell) to a known leg option. Matches either the
 * leg code (e.g. "C") or the full name (e.g. "Cone leg / Carbon"), case-insensitively.
 * Returns undefined for an empty or unrecognized value.
 */
export const findLegOption = (value: string): LegOption | undefined => {
  const needle = (value || "").trim().toLowerCase();
  if (!needle) return undefined;

  return LEG_OPTIONS.find(
    (option) => option.code.toLowerCase() === needle || option.name.toLowerCase() === needle
  );
};
